import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetReportsByAgencyId } from '../../../../api/agencies/useGetReportsByAgencyId'
import { useParams } from 'react-router'
import { AnimatePresence, motion, useSpring, useTransform } from 'framer-motion'
import {
  ChevronDown,
  ChevronUp,
  LoadingComponent,
  ActionDownload,
} from '@demandlocal/components'
import classNames from 'classnames'
import { ReactComponent as SubfolderIcon } from '../../../../images/icons/subfolder_icon.svg'
import { ReactComponent as FolderIcon } from '../../../../images/icons/folder_icon.svg'
import AgencyReportFiles from './AgencyReportFiles'
import toast from 'react-hot-toast'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { useDownloadAgencyReport } from '../../../../api/agencies/useDownloadAgencyReport'

export type ReportsPathElement = {
  agency_id?: string
  dealership?: {
    id: number
    name: string
  }
  path: string
  name: string | undefined
  raw_path: string
  next_prefix?: string | undefined
  type: 'file' | 'folder'
  url: string
}

export default function AgencyReportPath({
  pathElement,
  rootFolder = false,
}: {
  pathElement: ReportsPathElement
  rootFolder?: boolean
}): JSX.Element {
  const { agency_id } = useParams()
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: { data, path_type } = {}, isLoading } = useGetReportsByAgencyId({
    agency_id: Number(agency_id),
    prefix: pathElement?.next_prefix,
    enabled: isExpanded,
  })
  const zip = useMemo(() => new JSZip(), [])
  const reportsZip = zip.folder(`${pathElement?.dealership?.name}-${pathElement.path}`)

  const motionValue = useSpring(0)

  useEffect(() => {
    motionValue.set(isExpanded ? 1 : 0)
  }, [motionValue, isExpanded])

  const color = useTransform(motionValue, [1, 0], ['bg-white', 'bg-cultured-gray'])

  const containerClassNames = classNames(`rounded-lg border-cultured-gray`, {
    'border-2': rootFolder,
    'pt-2 pb-2': rootFolder,
    'pl-4': !rootFolder,
  })

  const titleClassNames = classNames(`text-xs`, {
    'font-bold': rootFolder,
    'font-medium': !rootFolder,
  })

  const { mutateAsync } = useDownloadAgencyReport()

  const downloadReport = useCallback(
    async (fileName: string, agency_id: string, path: string) => {
      try {
        await mutateAsync(
          {
            data: {
              agency_id: agency_id,
              path: path,
            },
          },
          {
            onSuccess: async (data) => {
              const blob = await data.blob()
              reportsZip?.file(fileName, blob)
            },
          },
        )
      } catch (e) {}
    },
    [mutateAsync, reportsZip],
  )

  const downloadFolder = useCallback(async () => {
    if (!data) return
    const toastId = toast.loading('Downloading...')
    try {
      for await (const file of data) {
        if (file.name && pathElement.agency_id)
          await downloadReport(file.name, pathElement.agency_id, file.path)
      }

      setTimeout(() => {
        zip.generateAsync({ type: 'blob' }).then(function (content) {
          saveAs(content, `${pathElement?.dealership?.name}-${pathElement.path}`)
        })
        toast.success('Done downloading!', {
          id: toastId,
        })
      }, 3000)
    } catch (err) {
      toast.error('Error downloading reports', {
        id: toastId,
      })
    }
  }, [
    data,
    downloadReport,
    pathElement.agency_id,
    pathElement?.dealership?.name,
    pathElement.path,
    zip,
  ])

  return (
    <motion.div
      className={classNames(`${containerClassNames} ${color}`)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}>
      <div
        className={`w-full flex items-center justify-between px-3 ${
          rootFolder ? '' : ' py-2'
        }`}
        onClick={() => setIsExpanded((prev) => !prev)}>
        <div className={'flex flex-row justify-between items-center w-full'}>
          <div className={'flex items-center gap-2'}>
            <motion.div layout layoutId={`folder-${pathElement?.raw_path}`}>
              {rootFolder ? (
                <FolderIcon className={'stroke-primary-xiketic'} />
              ) : (
                <SubfolderIcon />
              )}
            </motion.div>
            <h1 className={titleClassNames}>{pathElement?.path}</h1>
          </div>
          {path_type === 'file' && (
            <ActionDownload
              // @ts-ignore
              className={'fill-primary-persian-blue  cursor-pointer mr-3'}
              onClick={(e: Event): void => {
                downloadFolder()
                e.preventDefault()
                e.stopPropagation()
              }}
            />
          )}
        </div>
        <div className={'pl-3 border-gainsboro-gray border-l-2'}>
          {isExpanded ? (
            // @ts-ignore
            <ChevronUp className={'fill-primary-xiketic'} />
          ) : (
            // @ts-ignore
            <ChevronDown className={'fill-primary-xiketic'} />
          )}
        </div>
      </div>
      {!rootFolder && isExpanded && !isLoading && (
        <div className={'w-full border-b-2 ml-4'} />
      )}

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            layout={'preserve-aspect'}
            layoutId={pathElement?.raw_path}
            className={`relative overflow-hidden gap-2`}
            initial="collapsed"
            exit="collapsed"
            animate={'open'}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}>
            {isLoading && (
              <motion.div layout={'size'} className={'h-40'}>
                <LoadingComponent title={'Loading...'} />
              </motion.div>
            )}
            {path_type === 'folder' ? (
              data?.map((el) => {
                return (
                  <motion.div
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    layout={'size'}
                    layoutId={`expandedFolder-${el.raw_path}`}
                    className={'pt-2'}
                    key={el.raw_path}>
                    <AgencyReportPath pathElement={el} />
                  </motion.div>
                )
              })
            ) : (
              <AgencyReportFiles data={data} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
